import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {HttpMethods} from '@ngrx/data';
import {catchError, concatMap, take} from 'rxjs/operators';
import {Observable, throwError} from 'rxjs';
import {OccEndpointsService} from '@spartacus/core';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {DcpGlobalMessageService} from './dcp-global-message.service';
import {EndpointsService} from './endpoints.service';
import {DcpApiPlatforms, HttpRequestOptions} from '../models';
import {KymaService} from './kyma.service';

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class DcpApiService {
  constructor(
    private occEndpointsService: OccEndpointsService,
    private http: HttpClient,
    private kymaService: KymaService,
    private endpointsService: EndpointsService,
    private dcpGlobalMessageService: DcpGlobalMessageService,
  ) {}

  /**
   * To call commerce cloud Occ Api
   *
   * @param endpoint - to define new endpoint add new property to "dcpApiConfig.backend.occ.endpoints"
   * @param pathVariables - url variables (e.g ${var1}) will be replaced with given params (e.g {'var1':'value'})
   * @param options - will be passed to HttpClient.request(.., .., options)
   * @param httpMethod HTTP-Request Method
   */
  callOcc<T>(
    endpoint: string,
    pathVariables = {},
    options: HttpRequestOptions = {},
    httpMethod: HttpMethods | string = 'GET',
  ): Observable<T> {
    const url = this.occEndpointsService.buildUrl(endpoint, {
      urlParams: pathVariables,
    });
    return this.http.request<T>(httpMethod, url, options).pipe(
      catchError((e) => {
        this.dcpGlobalMessageService.addMessage(e, this?.constructor?.name, 'errorLogging');
        return throwError(() => e);
      }),
    );
  }

  /**
   * To call kyma lambdas and services
   *
   * @param namespace - to define new namespace add new property to "dcpApiConfig.kyma.namespace"
   * @param endpoint - to define new endpoint add new property to "dcpApiConfig.kyma[namespace].endpoint"
   * @param pathVariables - url variables (e.g ${var1}) will be replaced with given params (e.g {'var1':'value'})
   * @param options - will be passed to HttpClient.request(.., .., options)
   * @param httpMethod HTTP-Request Method
   */
  callKyma<T>(
    namespace: string,
    endpoint: string,
    pathVariables = {},
    options: HttpRequestOptions = {},
    httpMethod: HttpMethods | string = 'GET',
  ): Observable<T> {
    return this.kymaService.prepareAuthConfig().pipe(
      take(1),
      concatMap((kymaConfig) => {
        let mergedHeaders = kymaConfig.headers;
        const givenHeaders = options?.headers?.keys() ? options?.headers?.keys() : [];
        for (const headerName of givenHeaders) {
          mergedHeaders = mergedHeaders?.set(headerName, options.headers?.get(headerName) as string);
        }
        options.headers = mergedHeaders;
        const url = this.endpointsService.buildUrl(DcpApiPlatforms.KYMA, namespace, endpoint, pathVariables);
        return this.http.request<T>(httpMethod, url, options).pipe(
          catchError((e) => {
            this.dcpGlobalMessageService.addMessage(e, this?.constructor?.name, 'errorLogging');
            return throwError(() => e);
          }),
        );
      }),
      untilDestroyed(this),
    );
  }

  /**
   * To call APIs
   *
   * @param namespace - to define new namespace add new property to "dcpApiConfig.platform.namespace"
   * @param endpoint - to define new endpoint add new property to "dcpApiConfig.platform[namespace].endpoint"
   * @param pathVariables - url variables (e.g ${var1}) will be replaced with given params (e.g {'var1':'value'})
   * @param options - will be passed to HttpClient.request(.., .., options)
   * @param httpMethod HTTP-Request Method
   */
  callApi<T>(
    namespace: string,
    endpoint: string,
    pathVariables = {},
    options: HttpRequestOptions = {},
    httpMethod: HttpMethods | string = 'GET',
  ): Observable<T> {
    const url = this.endpointsService.buildUrl(DcpApiPlatforms.API, namespace, endpoint, pathVariables);
    return this.http.request<T>(httpMethod, url, options).pipe(
      catchError((e) => {
        this.dcpGlobalMessageService.addMessage(e, this?.constructor?.name, 'errorLogging');
        return throwError(() => e);
      }),
      untilDestroyed(this),
    );
  }
}
